import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { useBookingPopup } from "@/hooks/use-booking-popup";
import { useState, useEffect, useRef } from "react";
import {
  format,
  addMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isBefore,
  startOfToday,
  parseISO,
} from "date-fns";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";

interface LayoutProps {
  children: React.ReactNode;
}

const CALENDAR_ID = "WrG3tuspZH2fbYs61mnM";
const LOCATION_ID = "LybUg0eKBAfPYJVWJI73";
const VIBE_API_URL = "https://backend.leadconnectorhq.com/vibe-ai";

const Layout = ({ children }: LayoutProps) => {
  const { isOpen, closePopup, openPopup } = useBookingPopup();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [slots, setSlots] = useState<Record<string, { slots: string[] }>>({});
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [step, setStep] = useState<"calendar" | "form" | "success">("calendar");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timesRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedDate && timesRef.current) {
      // Smooth-scroll the times section into view within the dialog
      setTimeout(() => {
        timesRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 100);
    }
  }, [selectedDate]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dogName: "",
    breed: "",
    message: "",
  });

  useEffect(() => {
    if (isOpen && step === "calendar") {
      fetchSlots();
    }
  }, [isOpen, currentMonth, step]);

  const fetchSlots = async () => {
    setIsLoadingSlots(true);
    try {
      const start = startOfMonth(currentMonth).getTime();
      const end = endOfMonth(currentMonth).getTime();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch(
        `https://backend.leadconnectorhq.com/calendars/${CALENDAR_ID}/free-slots?startDate=${start}&endDate=${end}&timezone=${timezone}`,
      );
      const data = await response.json();
      setSlots(data);
    } catch (error) {
      console.error("Failed to fetch slots:", error);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;

    setIsSubmitting(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await fetch(`${VIBE_API_URL}/booking/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          locationId: LOCATION_ID,
          calendarId: CALENDAR_ID,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          notes: formData.message,
          selectedSlot,
          selectedTimezone: timezone,
          sessionId: crypto.randomUUID(),
          timezone,
          customFields: [
            {
              id: "O9O2OlIDG5IOnYvbnm4Z",
              key: "contact.dog_name",
              field_value: formData.dogName,
            },
            {
              id: "kJHx3NkYKJ3SKyQ4X81u",
              key: "contact.breed",
              field_value: formData.breed,
            },
          ],
        }),
      });

      if (response.ok) {
        setStep("success");
      }
    } catch (error) {
      console.error("Booking failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const availableSlotsForDate = selectedDate
    ? slots[format(selectedDate, "yyyy-MM-dd")]?.slots || []
    : [];

  const handleClose = () => {
    closePopup();
    // Reset state after a short delay to avoid flicker
    setTimeout(() => {
      setStep("calendar");
      setSelectedDate(null);
      setSelectedSlot(null);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        dogName: "",
        breed: "",
        message: "",
      });
    }, 300);
  };

  return (
    <div className="min-h-screen flex flex-col font-body">
      <Header />

      <main className="flex-grow">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          {children}
        </motion.div>
      </main>

      <Footer />

      {/* Floating Action Button */}
      <Button
        onClick={openPopup}
        className="fixed bottom-6 right-6 h-14 rounded-full shadow-2xl bg-accent hover:bg-accent/90 text-white px-8 font-bold uppercase tracking-wide z-50 flex items-center gap-2 border-2 border-white/20 transition-transform hover:scale-105"
      >
        <CalendarDays className="w-5 h-5" />
        Free Assessment
      </Button>

      {/* Global Popup - Free Assessment Call */}
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-[600px] p-0 overflow-y-auto overflow-x-hidden max-h-[90vh] border-none bg-white rounded-2xl [&>button]:text-white">
          <div className="bg-primary p-8 text-white text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <img
                src="https://vibe.filesafe.space/1780604500184904220/attachments/966faee0-985d-4a17-9ffd-2d5ba45ce4bd.png"
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <DialogHeader className="relative z-10">
              <DialogTitle className="text-2xl md:text-3xl font-display font-bold uppercase tracking-tight leading-tight">
                {step === "success"
                  ? "Booking Confirmed!"
                  : "Training should never feel intimidating."}
              </DialogTitle>
              {step !== "success" && (
                <DialogDescription className="text-white/80 text-sm md:text-base font-medium mt-2 max-w-md mx-auto">
                  Whether you're raising a puppy, struggling with reactivity, or
                  just wanting a calmer relationship, we're here to help.
                </DialogDescription>
              )}
            </DialogHeader>
          </div>

          <div ref={scrollContainerRef} className="p-6 md:p-8">
            {step === "calendar" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-primary uppercase tracking-wider flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-accent" />
                    Select a Date
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        setCurrentMonth(addMonths(currentMonth, -1))
                      }
                      disabled={isBefore(
                        startOfMonth(addMonths(currentMonth, -1)),
                        startOfMonth(new Date()),
                      )}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="font-bold text-sm min-w-[100px] text-center uppercase">
                      {format(currentMonth, "MMMM yyyy")}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        setCurrentMonth(addMonths(currentMonth, 1))
                      }
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-2 mb-6">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <div
                        key={day}
                        className="text-center text-[10px] font-bold text-primary/40 uppercase py-2"
                      >
                        {day}
                      </div>
                    ),
                  )}
                  {Array.from({
                    length: startOfMonth(currentMonth).getDay(),
                  }).map((_, i) => (
                    <div key={`empty-${i}`} />
                  ))}
                  {daysInMonth.map((day) => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    const hasSlots = slots[dateStr]?.slots.length > 0;
                    const isPast = isBefore(day, startOfToday());
                    const isSelected =
                      selectedDate && isSameDay(day, selectedDate);

                    return (
                      <button
                        key={dateStr}
                        disabled={!hasSlots || isPast}
                        onClick={() => setSelectedDate(day)}
                        className={cn(
                          "aspect-square rounded-lg flex items-center justify-center text-sm font-bold transition-all border-2",
                          isSelected
                            ? "bg-accent border-accent text-white"
                            : hasSlots
                              ? "bg-white border-accent/20 text-primary hover:border-accent"
                              : "bg-muted/30 border-transparent text-primary/20 cursor-not-allowed",
                        )}
                      >
                        {format(day, "d")}
                      </button>
                    );
                  })}
                </div>

                {selectedDate && (
                  <div
                    ref={timesRef}
                    className="space-y-4 animate-in fade-in slide-in-from-bottom-4 scroll-mt-4 border-t-2 border-accent/20 pt-6 mt-2"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-primary uppercase tracking-wider flex items-center gap-2">
                        <Clock className="w-5 h-5 text-accent" />
                        Available Times
                      </h3>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-accent bg-accent/10 px-2 py-1 rounded-full sm:hidden">
                        ↓ Scroll down
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlotsForDate.map((slot) => (
                        <button
                          key={slot}
                          onClick={() => setSelectedSlot(slot)}
                          className={cn(
                            "py-3 rounded-lg text-xs font-bold transition-all border-2",
                            selectedSlot === slot
                              ? "bg-primary border-primary text-white"
                              : "bg-white border-border text-primary hover:border-primary",
                          )}
                        >
                          {format(parseISO(slot), "h:mm a")}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-4">
                  <Button
                    disabled={!selectedSlot}
                    onClick={() => setStep("form")}
                    className="w-full bg-accent hover:bg-accent/90 text-white font-bold py-4 sm:py-6 h-auto whitespace-normal uppercase tracking-widest"
                  >
                    Continue to Details
                  </Button>
                </div>
              </div>
            )}

            {step === "form" && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="firstName"
                      className="text-xs font-bold uppercase tracking-wider text-primary/60"
                    >
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) =>
                        setFormData({ ...formData, firstName: e.target.value })
                      }
                      className="border-2 focus-visible:ring-accent"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="lastName"
                      className="text-xs font-bold uppercase tracking-wider text-primary/60"
                    >
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) =>
                        setFormData({ ...formData, lastName: e.target.value })
                      }
                      className="border-2 focus-visible:ring-accent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-xs font-bold uppercase tracking-wider text-primary/60"
                    >
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="border-2 focus-visible:ring-accent"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="phone"
                      className="text-xs font-bold uppercase tracking-wider text-primary/60"
                    >
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="border-2 focus-visible:ring-accent"
                    />
                  </div>
                </div>
                <div
                  className="grid grid-cols-2 gap-4"
                  data-custom-field="O9O2OlIDG5IOnYvbnm4Z"
                >
                  <div className="space-y-2">
                    <Label
                      htmlFor="dogName"
                      className="text-xs font-bold uppercase tracking-wider text-primary/60"
                    >
                      Dog's Name
                    </Label>
                    <Input
                      id="dogName"
                      required
                      value={formData.dogName}
                      onChange={(e) =>
                        setFormData({ ...formData, dogName: e.target.value })
                      }
                      className="border-2 focus-visible:ring-accent"
                    />
                  </div>
                  <div
                    className="space-y-2"
                    data-custom-field="kJHx3NkYKJ3SKyQ4X81u"
                  >
                    <Label
                      htmlFor="breed"
                      className="text-xs font-bold uppercase tracking-wider text-primary/60"
                    >
                      Breed
                    </Label>
                    <Input
                      id="breed"
                      required
                      value={formData.breed}
                      onChange={(e) =>
                        setFormData({ ...formData, breed: e.target.value })
                      }
                      className="border-2 focus-visible:ring-accent"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="message"
                    className="text-xs font-bold uppercase tracking-wider text-primary/60"
                  >
                    Tell us a bit about your dog
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    className="border-2 focus-visible:ring-accent min-h-[100px]"
                  />
                </div>
                <div className="pt-4 flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep("calendar")}
                    className="flex-1 py-4 sm:py-6 h-auto whitespace-normal font-bold uppercase tracking-widest border-2"
                  >
                    Back
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    type="submit"
                    className="flex-[2] bg-accent hover:bg-accent/90 text-white font-bold py-4 sm:py-6 h-auto whitespace-normal uppercase tracking-widest"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      "Confirm Booking"
                    )}
                  </Button>
                </div>
              </form>
            )}

            {step === "success" && (
              <div className="text-center py-12 space-y-6 animate-in zoom-in-95">
                <div className="flex justify-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-primary">
                    Your call is booked!
                  </h3>
                  <p className="text-primary/60 font-medium">
                    We've sent a confirmation email to{" "}
                    <span className="text-primary font-bold">
                      {formData.email}
                    </span>{" "}
                    with all the details.
                  </p>
                </div>
                <div className="bg-muted/30 p-6 rounded-xl space-y-3 max-w-sm mx-auto text-left">
                  <div className="flex items-center gap-3 text-sm">
                    <CalendarDays className="w-4 h-4 text-accent" />
                    <span className="font-bold">
                      {format(parseISO(selectedSlot!), "EEEE, MMMM do")}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="w-4 h-4 text-accent" />
                    <span className="font-bold">
                      {format(parseISO(selectedSlot!), "h:mm a")}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleClose}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold px-4 py-6 h-auto whitespace-normal uppercase tracking-widest"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
